import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import { Card } from '~/components/ui/card'
import { cn } from '~/lib/utils'

// 지표 계기판 타일. 모든 카드가 동일 구조 — 좌측 3px 세로 강조선(항상 존재)으로
// 카드 간 일관성을 확보하고, tone은 강조선 + 아이콘 틴트에만 반영한다(상단 색띠 제거).
export type StatTone =
    | 'default'
    | 'primary'
    | 'success'
    | 'warning'
    | 'danger'
    | 'info'

// 좌측 3px 세로 강조선 색. default는 중립 border색 → 모든 카드에 항상 선이 존재.
const TONE_BAR: Record<StatTone, string> = {
    default: 'bg-border',
    primary: 'bg-primary',
    success: 'bg-success',
    warning: 'bg-warning',
    danger: 'bg-destructive',
    info: 'bg-info',
}

// 아이콘 타일 틴트(subtle 표면 + 의미색). default는 중립 표면. 정사각(각진) 타일.
const TONE_ICON: Record<StatTone, string> = {
    default: 'bg-secondary text-muted-foreground',
    primary: 'bg-accent text-accent-foreground',
    success: 'bg-success-subtle text-success',
    warning: 'bg-warning-subtle text-warning',
    danger: 'bg-destructive/10 text-destructive',
    info: 'bg-info-subtle text-info',
}

export function StatCard({
    label,
    value,
    hint,
    icon: Icon,
    tone = 'default',
}: {
    label: string
    value: number | string
    hint?: ReactNode
    icon?: LucideIcon
    tone?: StatTone
}) {
    return (
        <Card className='relative overflow-hidden p-3.5 shadow-none'>
            {/* 좌측 3px 세로 강조선 — tone 색(default=중립). 모든 카드에 항상 존재. */}
            <span
                aria-hidden='true'
                className={cn(
                    'absolute inset-y-0 left-0 w-[3px]',
                    TONE_BAR[tone]
                )}
            />
            <div className='flex items-center gap-2'>
                {Icon ? (
                    <span
                        className={cn(
                            'flex size-7 shrink-0 items-center justify-center rounded-[var(--radius-sm)]',
                            TONE_ICON[tone]
                        )}>
                        <Icon className='size-4' />
                    </span>
                ) : null}
                <div className='admin-eyebrow min-w-0 truncate'>{label}</div>
            </div>
            <div className='text-foreground tnum mt-2 text-2xl font-semibold tracking-tight'>
                {value}
            </div>
            {hint ? (
                <div className='text-muted-foreground mt-1 text-[12px]'>
                    {hint}
                </div>
            ) : null}
        </Card>
    )
}
